<script setup>
import { onMounted, ref, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { appBarStore } from "@/stores/appBar";
const appBar = appBarStore();
import { permissionsStore } from "@/stores/permissions";
import { apiQuery } from "@/api/j24api";
const permissions = permissionsStore();
const items = ref([]);
import { storeToRefs } from "pinia";
import { patch24Store } from "@/stores/patch24";
const patch24 = patch24Store();
const { selectedLicense, alerts } = storeToRefs(patch24);

const router = useRouter();
const route = useRoute();
const eulaAccepted = ref(true);

const loading = ref(false);
permissions.$subscribe(() => {
  buildSidebarItems();
});

appBar.$subscribe(async () => {
  console.log("Sidebar Selected License Changed", appBar.$state);
  patch24.$reset();
  if (appBar.selected) {
    await getLicense(appBar.selected);
  }
});

// Watch for route changes to detect when user returns from EULA page
watch(
  () => route.query.eulaAccepted,
  async (newValue) => {
    if (newValue === "true" && appBar.selected) {
      console.log("EULA accepted, refreshing license");
      eulaAccepted.value = true;
      await getLicense(appBar.selected);
      // Clean up the query parameter
      router.replace({ query: {} });
    }
  }
);

onMounted(async () => {
  appBar.$reset();
  if (appBar.appRunning != "PatchingV2") {
    appBar.$patch({
      appRunning: "PatchingV2",
      type: "select",
      items: [],
      selected: "",
      header: "Loading Please Wait...",
    });
  }
  buildSidebarItems();
  buildPatchLicensesList();
});

const patchPolicySet = computed(() => {
  if (!selectedLicense.value.name) {
    return false;
  }
  if (selectedLicense.value.GlobalConfiguration) {
    return false;
  } else {
    return true;
  }
});

async function getLicense(id) {
  loading.value = true;
  try {
    var result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingLicense",
      id: id,
    });
    if (result.length) {
      selectedLicense.value = result[0];

      // Check if EULA has been accepted
      if (!selectedLicense.value.eulaAccepted) {
        eulaAccepted.value = false;
        // Redirect to EULA page with license ID
        router.push({
          name: "PatchingV2EULA",
          query: { licenseId: selectedLicense.value._id },
        });
        loading.value = false;
        return;
      } else {
        // EULA is accepted, show normal sidebar items
        eulaAccepted.value = true;
      }
    } else {
      alerts.value.push({
        type: "error",
        message: "Error getting license",
        closable: true,
      });
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting license",
      closable: true,
    });
  }
  loading.value = false;
}

function buildSidebarItems() {
  items.value.length = 0;
  allItems.forEach((item) => {
    if (permissions.$state["PatchingV2-" + item.privName]) {
      items.value.push(item);
    }
  });
}

async function buildPatchLicensesList() {
  var licenses = await getPatchLicenses();
  for (let key in licenses) {
    licenses[key].title = licenses[key].name;
    licenses[key].value = licenses[key]._id;
  }
  if (!appBar.selected & (licenses.length == 1)) {
    appBar.$patch({
      items: licenses,
      header: "Select Patch License",
      selected: licenses[0].value,
    });
  } else {
    appBar.$patch({
      items: licenses,
      header: "Select Patch License",
    });
  }
  localStorage.setItem("patchingV2SelectedLicense", JSON.stringify(licenses));
}

async function getPatchLicenses() {
  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingLicenses",
    });
    console.log(result);
    return result;
  } catch (error) {
    console.log(error);
    return [];
  }
}

const allItems = [
  // { title: "Assets", icon: "mdi-archive", link: "AssetsBulkUploader" },
  {
    title: "Dashboard",
    icon: "mdi-view-dashboard",
    link: "PatchingV2Dashboard",
    value: true,
    privName: "Dashboard",
  },
  {
    title: "App Management",
    icon: "mdi-apps",
    link: "PatchingV2AppManagement",
    value: false,
    privName: "Dashboard",
  },
  {
    title: "Devices",
    icon: "mdi-cellphone-link",
    link: "PatchingV2Devices",
    value: false,
    privName: "Dashboard",
  },
  {
    title: "Patch Policy",
    icon: "mdi-shield-search",
    link: "PatchingV2Policy",
    privName: "Dashboard",
  },
  {
    title: "Command Viewer",
    icon: "mdi-apple-keyboard-command",
    link: "PatchingV2Commands",
    privName: "Dashboard",
  },
  {
    title: "Support",
    icon: "mdi-chat-question-outline",
    link: "PatchingV2Support",
    privName: "Dashboard",
  },
];

// Required to make the router-view reload when user clicks the link again
// See the key value set on <router-view> below.
const randomKey = ref("");
function updateRandom() {
  randomKey.value = (Math.random() + 1).toString(36).substring(7);
}
</script>

<template>
  <v-navigation-drawer permanent width="220" theme="jigsawSidebar">
    <v-list class="pt-16">
      <template v-if="!eulaAccepted">
        <v-list-item
          class="sideBarListItem"
          link
          :to="{ name: 'PatchingV2EULA' }"
          variant="plain"
          @click="updateRandom()"
        >
          <template v-slot:prepend>
            <v-icon class="mr-2">mdi-file-document-outline</v-icon>
          </template>
          <v-list-item-title class="pl-n4">
            End User License Agreement
          </v-list-item-title>
        </v-list-item>
      </template>
      <template v-else>
        <template v-for="item in items" :key="item.title">
          <v-list-item
            class="sideBarListItem"
            link
            :to="{ name: item.link }"
            variant="plain"
            @click="updateRandom()"
          >
            <template v-slot:prepend>
              <v-icon class="mr-2">{{ item.icon }}</v-icon>
            </template>
            <v-list-item-title class="pl-n4">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </template>
    </v-list>
  </v-navigation-drawer>
  <v-main theme="jigsawNew">
    <v-container fluid>
      <router-view :key="randomKey" />
    </v-container>
  </v-main>

  <v-card
    v-if="patchPolicySet && eulaAccepted"
    class="position-fixed"
    style="bottom: 20px; right: 20px; z-index: 1000"
    elevation="8"
    width="300"
    rounded="lg"
    color="#1D2939"
    title="Patch Policy not set"
  >
    <template v-slot:prepend>
      <v-icon size="x-large" color="error" icon="mdi-alert-outline"></v-icon>
    </template>
    <v-card-text
      >Devices will not receive updates until a Patch Policy is configured and
      you press
      <span class="font-weight-bold">save</span>
      to apply the changes.
      <v-row class="mt-2">
        <v-spacer></v-spacer>
        <v-col>
          <v-btn
            color="surface-variant"
            @click="$router.push({ name: 'PatchingV2Policy' })"
            >Patch Policy</v-btn
          >
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.sideBarListItem :deep(.v-list-item__prepend .v-list-item__spacer) {
  width: 10px !important;
  /* color: white !important; */
}
.v-list-item--active {
  background-color: #e7541e !important;
}
.v-list-item--variant-plain {
  opacity: 1 !important;
}
</style>
